import { Dispatch, SetStateAction, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash-es';
import c from 'classnames';

import { Icon, Typography } from '@repo/ui';
import DesignButton from 'components/DesignButton/DesignButton';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';

import styles from './NewFilters.module.scss';

export type FilterConfig = {
  value: string;
  display: string | React.ReactElement;
  count?: number;
};

type FilterProps = {
  name: string;
  filters: string[];
  filterConfig: FilterConfig[];
  setFiltered:
    | ((filters: string[]) => Promise<void>)
    | Dispatch<SetStateAction<string[]>>;
  className?: string;
};

export const NewFilters = ({
  name,
  filters,
  filterConfig,
  setFiltered,
  className,
}: FilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();

  const toggleFilter = (filterValue: string) => {
    trackEvent('Clicked Button', {
      text:
        filterConfig.find(f => f.value === filterValue)?.display?.toString() ||
        filterValue,
      href: '',
      isExternal: false,
      variety: 'Filter',
    });

    let updatedFilters: string[];
    if (filters?.includes(filterValue)) {
      updatedFilters = filters.filter(
        currentFilter => currentFilter !== filterValue,
      );
    } else {
      updatedFilters = [...(filters || []), filterValue];
    }
    setFiltered(updatedFilters);
  };

  const handleResetFilters = useCallback(() => {
    trackEvent('Clicked Button', {
      text: 'Reset filters',
      href: '',
      isExternal: false,
      variety: 'Filter Reset',
    });
    setFiltered(['red', 'orange', 'grey', 'green']);
  }, [setFiltered, trackEvent]);

  useEffect(() => {
    const setParams = () => {
      if (filters.length) {
        setSearchParams({ [name]: filters.join(',') }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    };
    setParams();
  }, [filters, filterConfig.length, name, setSearchParams]);

  useEffect(() => {
    const paramFilters = searchParams.get(name);
    if (paramFilters) {
      const filterValues = paramFilters.split(',');
      if (!isEqual(filters, filterValues)) {
        setFiltered(filterValues);
      }
    }
  }, [searchParams, name, setFiltered]);

  return (
    <div className={c(styles.filtersContainer, className)}>
      <div className={styles.iconPlaceholder}>
        <Icon name='filterLines' />
      </div>

      {filterConfig.map(({ value, display, count }) => (
        <div
          key={value}
          className={c(
            styles.filter,
            filters?.includes(value) ? styles.active : styles.inactive,
          )}
          onClick={() => toggleFilter(value)}
        >
          {display}
          {count !== undefined && (
            <Typography variant='label' className={styles.count}>
              ({count})
            </Typography>
          )}
        </div>
      ))}

      <div className={c(styles.resetButtonContainer, 'hide-on-desktop')}>
        <DesignButton
          size='small'
          variant='wrapper'
          onClick={handleResetFilters}
          iconNameLeft='closeSm'
          label='Reset filters'
        />
      </div>
    </div>
  );
};
